import React, { StrictMode } from 'react'
import ReactDOM from 'react-dom'
import { compose } from 'redux'
import { Provider } from 'react-redux'
import { resetContext, getContext } from 'kea'
import Modal from 'react-modal'

import plugins from 'lib/plugins'

import { initBugSnag } from './bugsnag'
import { initLocale } from './locale'
import Layout from './layout'
import bundles from './bundles-async'

if (typeof window !== 'undefined') {
  window.__KEA_GET_CONTEXT__ = getContext
}

Modal.setAppElement('#wrap')

const TRACE_LIMIT = 100

const devMode = process.env.NODE_ENV === 'development'

// Using trace feature in Redux devTools in development environment
const reduxDevToolsCompose =
  typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ && devMode
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({ trace: true, traceLimit: TRACE_LIMIT })
    : compose

// Main app on all full-screen React pages
const AppContainer = async (props, railsContext, domNodeId) => {
  const { scene } = railsContext

  resetContext({
    plugins: plugins(),
    sceneDefaults: {
      [scene]: props
    },
    createStore: {
      reducers: {
        rails: () => railsContext
      },
      compose: reduxDevToolsCompose
    }
  })

  initLocale(railsContext.i18nLocale)
  initBugSnag(railsContext)

  const splitPoints = ['header', 'footer', scene]
  const response = await Promise.all(splitPoints.map(chunk => bundles[chunk].loadComponent()))
  const Header = response[0]
  const Footer = response[1]
  const reactElement = <Layout bundles={bundles} railsContext={railsContext} Header={Header} Footer={Footer} />
  ReactDOM.hydrate(<StrictMode>{reactElement}</StrictMode>, document.getElementById(domNodeId))
}

// Used on old Rails pages that need the React header
const HeaderContainer = async (props, railsContext, domNodeId) => {
  resetContext({
    plugins: plugins(),
    sceneDefaults: {},
    createStore: {
      reducers: {
        rails: () => railsContext
      },
      compose: reduxDevToolsCompose
    }
  })

  initLocale(railsContext.i18nLocale)
  initBugSnag(railsContext)

  const scene = 'header'
  const splitPoints = [scene]
  const response = await Promise.all(splitPoints.map(chunk => bundles[chunk].loadComponent()))
  const Header = response[0]
  const reactElement = (
    <Provider store={getContext().store}>
      <Header {...railsContext.headerProps} />
    </Provider>
  )
  ReactDOM.hydrate(<StrictMode>{reactElement}</StrictMode>, document.getElementById(domNodeId))
}

// Used on old rails pages that need the React footer
const FooterContainer = async (props, railsContext, domNodeId) => {
  resetContext({
    plugins: plugins(),
    sceneDefaults: {},
    createStore: {
      reducers: {
        rails: () => railsContext
      },
      compose: reduxDevToolsCompose
    }
  })

  initLocale(railsContext.i18nLocale)
  initBugSnag(railsContext)

  const scene = 'footer'
  const splitPoints = [scene]
  const response = await Promise.all(splitPoints.map(chunk => bundles[chunk].loadComponent()))
  const Footer = response[0]

  const reactElement = (
    <Provider store={getContext().store}>
      <Footer {...railsContext.footerProps} />
    </Provider>
  )
  ReactDOM.hydrate(<StrictMode>{reactElement}</StrictMode>, document.getElementById(domNodeId))
}

export default { AppContainer, HeaderContainer, FooterContainer }
